import type { Editor } from "@tiptap/core";
import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { Asset } from "schemas/generated/asset";
import type { Product } from "schemas/generated/product";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/tabs/Tabs";
import { skipToken } from "@tanstack/react-query";
import { ACCEPT_FILE_EXTENSIONS } from "@v2/features/assets/constants";
import { useReploFileDropZone } from "@v2/features/assets/useFileDropZone";
import { useChatFileDropZone } from "@v2/features/chat/hooks/useChatFileDropZone";
import {
  selectAsset,
  selectIntegration,
  selectProduct,
} from "@v2/features/chat/utils/contextItem";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useProductList } from "@v2/hooks/useProductList";
import { assetUtils } from "@v2/trpc/routes/asset";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { ImageIcon, Plug, Search, ShoppingBag, Upload, X } from "lucide-react";
import { motion } from "motion/react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";
import { z } from "zod";

import { AssetContextItemsList } from "./AssetContextItemsList";
import { IntegrationContextItemsList } from "./IntegrationContextItemsList";
import { ProductContextItemsList } from "./ProductContextItemsList";

const INITIAL_ITEMS_TO_LOAD = 10;

interface ContextItemsMenuProps {
  onClose?: () => void;
  editor: Editor | null;
  hideHeader?: boolean;
  inputSize?: "sm" | "base";
}

type ContextItemsMenuTab = "assets" | "products" | "integrations";

export const ContextItemsMenu = ({
  onClose,
  editor,
  hideHeader,
  inputSize = "sm",
}: ContextItemsMenuProps) => {
  const projectId = useCurrentProjectId();
  const [currentTab, setCurrentTab] = useLocalStorageState(
    "replo.editor.contextItemsMenu.currentTab",
    "products" as ContextItemsMenuTab,
    { schema: z.enum(["products", "assets", "integrations"]) },
  );
  const [searchInputValue, searchQuery, setSearchInputValue] =
    useDebouncedState("");
  const menuRef = React.useRef<HTMLDivElement>(null);

  // NOTE (Kevin, 2025-12-15): Track if upload came from global drop to auto-select the asset
  const isExternalUploadRef = React.useRef(false);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    droppedFilesMap,
    openFileDialog,
    uploadFiles,
  } = useReploFileDropZone({
    onUploadComplete: ({ url, file, id }) => {
      void assetUtils.list.invalidate();
      // NOTE (Kevin, 2025-12-15): Auto-select asset after upload from global drop
      if (isExternalUploadRef.current && file && id) {
        isExternalUploadRef.current = false;
        const asset: Asset = {
          id,
          url,
          name: file.name,
          type: file.type.startsWith("video/") ? "video" : "image",
          fileExtension: file.name.split(".").pop() ?? "",
          createdAt: new Date(),
          updatedAt: new Date(),
          sizeBytes: file.size,
          width: null,
          height: null,
          projectId: projectId ?? "",
          altText: null,
          embeddingText: null,
        };
        selectAsset(asset, editor);
        onClose?.();
      }
    },
    acceptDropAssetType: ACCEPT_FILE_EXTENSIONS,
    allowClickToUpload: true,
    // NOTE (Kevin, 2025-12-15): Disable local dropzone since global interceptor handles all drops
    // to prevent duplicate uploads when dropping files on the assets grid
    isDisabled: true,
  });

  // NOTE (Kevin, 2025-12-15): Register as an interceptor for global file drops when menu is open
  useChatFileDropZone({
    isActive: true,
    acceptedTypes: ["image", "video"],
    onFileDrop: (files) => {
      // NOTE (Kevin, 2025-12-15): Switch to assets tab when files are dropped
      setCurrentTab("assets");
      isExternalUploadRef.current = true;
      uploadFiles(files);
    },
  });

  const {
    products,
    isLoading: isLoadingProducts,
    fetchNextPage: fetchNextProductsPage,
    hasNextPage: hasNextProductsPage,
  } = useProductList({
    projectId,
    limit: INITIAL_ITEMS_TO_LOAD,
    search: searchQuery,
  });

  const { data: integrations, isLoading: isLoadingIntegrations } =
    integrationRouter.find.useQuery(projectId ? { projectId } : skipToken);

  const tabs = [
    {
      value: "products",
      label: "Products",
      icon: <ShoppingBag className="size-3" />,
    },
    {
      value: "assets",
      label: "Assets",
      icon: <ImageIcon className="size-3" />,
    },
    {
      value: "integrations",
      label: "Integrations",
      icon: <Plug className="size-3" />,
    },
  ];

  const handleProductSelect = (product: Product) => {
    selectProduct(product, editor);
    onClose?.();
  };

  const handleAssetSelect = (asset: Asset) => {
    selectAsset(asset, editor);
    onClose?.();
  };

  const handleIntegrationSelect = (integrationType: IntegrationType) => {
    selectIntegration(integrationType, editor);
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full">
      {!hideHeader && (
        <div className="flex flex-row justify-between items-center p-3 border-b-0.5 border-border">
          <span className="font-medium text-default text-sm">
            Add a reference
          </span>
          <Button icon variant="tertiary" size="sm" onClick={onClose}>
            <X />
          </Button>
        </div>
      )}

      <motion.div
        className="overflow-hidden max-h-[400px]"
        animate={{ height: "auto" }}
        initial={false}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <Tabs
          value={currentTab}
          onValueChange={(value) => setCurrentTab(value as ContextItemsMenuTab)}
          orientation="vertical"
          className="h-full"
        >
          <div className="p-3 border-r-0.5 border-border">
            <TabsList size="sm" className="h-full">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  size="sm"
                  startEnhancer={tab.icon}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="w-full px-1 py-3 flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex flex-row items-center gap-2 px-2">
              <Input
                placeholder="Search"
                startEnhancer={<Search size={14} />}
                size={inputSize}
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                className="w-full"
                autoFocus={true}
                onKeyDown={(e) => {
                  // Handle tab navigation with arrow keys
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    // Focus the first item in the current list
                    const listContainer = menuRef.current?.querySelector(
                      `[data-list-container="${currentTab}"]`,
                    ) as HTMLElement;
                    if (listContainer) {
                      listContainer.focus();
                      // Trigger focus first item by dispatching ArrowDown
                      listContainer.dispatchEvent(
                        new KeyboardEvent("keydown", {
                          key: "ArrowDown",
                          bubbles: true,
                          cancelable: true,
                        }),
                      );
                    }
                    return;
                  }

                  if (e.key === "Escape") {
                    onClose?.();
                  }
                }}
              />
              {currentTab === "assets" && (
                <Button
                  icon
                  variant="secondary"
                  onClick={openFileDialog}
                  size="sm"
                >
                  <Upload />
                </Button>
              )}
            </div>
            <TabsContent
              value="products"
              className="h-full flex-1"
              id="context-menu-scroll-container"
            >
              <div className="h-full overflow-y-auto">
                <ProductContextItemsList
                  products={products}
                  isLoading={isLoadingProducts}
                  searchQuery={searchQuery}
                  fetchNextProductsPage={() => void fetchNextProductsPage()}
                  hasNextProductsPage={hasNextProductsPage}
                  onProductSelect={handleProductSelect}
                />
              </div>
            </TabsContent>
            <TabsContent value="assets" className="flex-1 min-h-0">
              <div className="h-full overflow-hidden">
                <AssetContextItemsList
                  onAssetSelect={handleAssetSelect}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  droppedFilesMap={droppedFilesMap}
                  isDragActive={isDragActive}
                  searchQuery={searchQuery}
                />
              </div>
            </TabsContent>
            <TabsContent
              value="integrations"
              className="flex-1 min-h-0 flex"
              UNSAFE_className="flex-col"
              id="context-menu-scroll-container-integrations"
            >
              <div className="flex-1 min-h-0 overflow-y-auto">
                <IntegrationContextItemsList
                  integrations={integrations ?? []}
                  isLoading={isLoadingIntegrations}
                  searchQuery={searchQuery}
                  onIntegrationSelect={handleIntegrationSelect}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </motion.div>
    </div>
  );
};
